/**
 * The field registry. Appendix X.1.
 *
 * > **Field namespaces** — the only fields that exist. **An unknown field is a validation error, not
 * > null.**
 *
 * That second sentence is the whole reason this file is a registry and not a `Record<string,
 * unknown>` lookup. A rule referencing `customer.segement` must fail loudly when it is saved, not
 * evaluate to null and quietly stop matching: a promotion that silently never applies is
 * indistinguishable from one nobody has redeemed, and a commission rule that silently never applies
 * is somebody's pay.
 *
 * Every field below is transcribed from Appendix X.1's table. Nothing is added, and the types are
 * what decide which operators are legal on each.
 */
export const FIELD_TYPES = ['money', 'number', 'string', 'string_list', 'boolean', 'date'];
/**
 * Appendix X.1's seven namespaces, verbatim.
 *
 * | Namespace | Fields |
 * |---|---|
 * | `order` | total, subtotal, line_count, pair_index, channel, branch_id, date, deposit_taken |
 * | `line` | category, product_id, brand, lens_design, lens_index, treatment_ids, unit_price, quantity, margin, cost |
 * | `rx` | sph_max, cyl_max, add, is_progressive, has_prism |
 * | `customer` | segment, tags, is_new, last_purchase_days, lifetime_value, loyalty_tier |
 * | `staff` | id, role |
 * | `date` | weekday, hour, is_holiday |
 * | `stock` | age_days, quantity_on_hand |
 */
export const FIELD_REGISTRY = {
    'order.total': { type: 'money', note: 'minor units' },
    'order.subtotal': { type: 'money', note: 'minor units' },
    'order.line_count': { type: 'number' },
    'order.pair_index': { type: 'number' },
    'order.channel': { type: 'string' },
    'order.branch_id': { type: 'string' },
    'order.date': { type: 'date', note: 'branch-local' },
    'order.deposit_taken': { type: 'boolean' },
    'line.category': { type: 'string' },
    'line.product_id': { type: 'string' },
    'line.brand': { type: 'string' },
    'line.lens_design': { type: 'string' },
    'line.lens_index': { type: 'number' },
    'line.treatment_ids': { type: 'string_list' },
    'line.unit_price': { type: 'money', note: 'minor units' },
    'line.quantity': { type: 'number' },
    'line.margin': { type: 'money', note: 'minor units' },
    'line.cost': { type: 'money', note: 'minor units' },
    'rx.sph_max': { type: 'number' },
    'rx.cyl_max': { type: 'number' },
    'rx.add': { type: 'number' },
    'rx.is_progressive': { type: 'boolean' },
    'rx.has_prism': { type: 'boolean' },
    'customer.segment': { type: 'string' },
    'customer.tags': { type: 'string_list' },
    'customer.is_new': { type: 'boolean' },
    'customer.last_purchase_days': { type: 'number' },
    'customer.lifetime_value': { type: 'money', note: 'minor units' },
    'customer.loyalty_tier': { type: 'string' },
    'staff.id': { type: 'string' },
    'staff.role': { type: 'string' },
    'date.weekday': { type: 'number', note: 'branch-local' },
    'date.hour': { type: 'number', note: 'branch-local' },
    'date.is_holiday': { type: 'boolean' },
    'stock.age_days': { type: 'number' },
    'stock.quantity_on_hand': { type: 'number' },
};
export const FIELD_NAMES = Object.keys(FIELD_REGISTRY);
export function isKnownField(field) {
    return Object.hasOwn(FIELD_REGISTRY, field);
}
/**
 * Which operators Appendix X.1 lists, and which of them make sense on each type.
 *
 * The appendix gives the operator list and the field types and does not join them, so this table is
 * derived rather than transcribed, and it is derived conservatively: an operator is allowed on a type
 * only where the comparison has one obvious meaning.
 *
 * `<` on a string is the case worth naming. It has a defined answer in JavaScript and it is
 * lexicographic, which is almost never what a rule author writing `customer.loyalty_tier < 'gold'`
 * means: they mean an ordering that the tier names do not carry. Refused at validation, which is
 * where `X.4` says rules are checked.
 */
export const OPERATORS = [
    '=',
    '!=',
    '<',
    '<=',
    '>',
    '>=',
    'in',
    'not_in',
    'between',
    'exists',
    'matches',
];
const ORDERED = ['<', '<=', '>', '>=', 'between'];
const EQUALITY = ['=', '!='];
const MEMBERSHIP = ['in', 'not_in'];
export function operatorsFor(type) {
    switch (type) {
        case 'money':
        case 'number':
        case 'date':
            return [...EQUALITY, ...ORDERED, ...MEMBERSHIP, 'exists'];
        case 'string':
            // `matches` is here and nowhere else: Appendix X.4 forbids user-supplied code, so it is a
            // literal pattern against a string and never a regular expression a rule author can write.
            return [...EQUALITY, ...MEMBERSHIP, 'exists', 'matches'];
        case 'boolean':
            return [...EQUALITY, 'exists'];
        case 'string_list':
            // `in` on a list field asks "does the list contain this", which is the only reading that is
            // useful for `line.treatment_ids` and `customer.tags`.
            return [...MEMBERSHIP, 'exists'];
    }
}
//# sourceMappingURL=fields.js.map